import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Sidebar from '../components/Sidebar';
import WelcomeScreen from '../components/WelcomeScreen';
import ChatMessages from '../components/ChatMessages';
import ChatInput from '../components/ChatInput';
import { useAuth } from '../context/AuthContext';
import { CATEGORIES } from '../mock';
import { apiListChats, apiSendChat, apiDeleteChat, apiGetChat } from '../api';
import { Menu } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { AnnouncementNotifications } from '../components/Announcements';
import HCLogo from '../components/HCLogo';
import AppSwitcher from '../components/AppSwitcher';
import { logError } from '../lib/logger';

const ChatPage = () => {
  const { chatId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation();
  const [chats, setChats] = useState([]);
  const [activeChatId, setActiveChatId] = useState(chatId || null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const endRef = useRef(null);

  const activeChat = chats.find(c => c.id === activeChatId);

  useEffect(() => {
    apiListChats().then(setChats).catch((e) => logError('Failed to list chats:', e));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (chatId && chatId !== activeChatId) {
      setActiveChatId(chatId);
      apiGetChat(chatId)
        .then((full) => setChats(prev => {
          const exists = prev.find(c => c.id === full.id);
          return exists ? prev.map(c => c.id === full.id ? full : c) : [full, ...prev];
        }))
        .catch((e) => logError('Failed to get chat:', e));
    }
  }, [chatId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [activeChat?.messages?.length, loading]);

  const handleNewChat = () => {
    setActiveChatId(null);
    setSelectedCategory(null);
    navigate('/');
    setSidebarOpen(false);
  };

  const handleSelectChat = async (id) => {
    setActiveChatId(id);
    navigate(`/chat/${id}`);
    setSidebarOpen(false);
    try {
      const full = await apiGetChat(id);
      setChats(prev => prev.map(c => c.id === id ? full : c));
    } catch (e) {
      logError('Failed to load chat:', e);
    }
  };

  const handleDeleteChat = async (id) => {
    try {
      await apiDeleteChat(id);
      setChats(prev => prev.filter(c => c.id !== id));
      if (activeChatId === id) {
        setActiveChatId(null);
        navigate('/');
      }
      toast.success(t('sidebar.deleteChatTitle'));
    } catch (e) {
      toast.error(t('common.deleteError'));
    }
  };

  const handleSend = async (content, attachments = []) => {
    if (!content.trim() && attachments.length === 0) return;
    const text = content.trim();
    setLoading(true);

    const category = activeChat?.category || selectedCategory;
    const optimisticUser = {
      role: 'user',
      content: text,
      attachments: attachments.map(f => ({ name: f.name, size: f.size })),
      timestamp: new Date().toISOString()
    };

    if (activeChatId && activeChatId !== '_temp') {
      setChats(prev => prev.map(c =>
        c.id === activeChatId
          ? { ...c, messages: [...(c.messages || []), optimisticUser] }
          : c
      ));
    } else {
      setActiveChatId('_temp');
      setChats(prev => [
        { id: '_temp', title: text.slice(0, 40), category, messages: [optimisticUser] },
        ...prev.filter(c => c.id !== '_temp')
      ]);
    }

    try {
      const res = await apiSendChat({
        content: text,
        category,
        chat_id: activeChatId && activeChatId !== '_temp' ? activeChatId : null
      });
      const fullChat = res.chat;
      setChats(prev => {
        const without = prev.filter(c => c.id !== '_temp' && c.id !== fullChat.id);
        return [fullChat, ...without];
      });
      setActiveChatId(fullChat.id);
      navigate(`/chat/${fullChat.id}`, { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('chat.errorSending'));
      setChats(prev => prev.filter(c => c.id !== '_temp'));
      if (activeChatId === '_temp') setActiveChatId(null);
    } finally {
      setLoading(false);
    }
  };

  const handleCategorySelect = (catId) => setSelectedCategory(catId);

  const activeCategoryObj = CATEGORIES.find(c =>
    c.id === (activeChat?.category || selectedCategory)
  );

  return (
    <div className="h-screen w-screen flex bg-white overflow-hidden">
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-slate-900/40 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      <Sidebar
        chats={chats}
        activeChatId={activeChatId}
        onNewChat={handleNewChat}
        onSelectChat={handleSelectChat}
        onDeleteChat={handleDeleteChat}
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
      />

      <main className="flex-1 flex flex-col min-w-0 bg-white">
        <header className="h-14 border-b border-slate-200 flex items-center justify-between px-4 lg:px-6 bg-white/80 backdrop-blur sticky top-0 z-20">
          <div className="flex items-center gap-3 min-w-0">
            <Button variant="ghost" size="icon" className="lg:hidden -ml-2" onClick={() => setSidebarOpen(true)}>
              <Menu className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-2 min-w-0">
              <HCLogo size="sm" />
              <div className="min-w-0">
                <div className="text-sm font-semibold text-slate-900 truncate">
                  {activeChat?.title || t('chat.newChatTitle')}
                </div>
                {activeCategoryObj && (
                  <div className="text-xs text-slate-500 truncate">
                    {t('welcome.categories')}: {activeCategoryObj.title}
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-50 border border-blue-100">
              <div className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
              <span className="text-xs font-medium text-blue-900">Gemini 2.5 Flash</span>
            </div>
            <AnnouncementNotifications />
          </div>
        </header>

        <AppSwitcher />

        <div className="flex-1 overflow-y-auto custom-scroll">
          {!activeChat || !activeChat.messages || activeChat.messages.length === 0 ? (
            <WelcomeScreen
              user={user}
              selectedCategory={selectedCategory}
              onSelectCategory={handleCategorySelect}
              onQuickPrompt={(p) => handleSend(p)}
            />
          ) : (
            <ChatMessages messages={activeChat.messages} loading={loading} endRef={endRef} />
          )}
        </div>

        <ChatInput onSend={handleSend} loading={loading} />
      </main>
    </div>
  );
};

export default ChatPage;
